import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, IconButton, Typography } from '@mui/material';
import { CloseCircle } from 'assets';
import { DatePickerField, InputField } from 'components';
import CheckboxField from 'components/form-control/CheckboxField';
import yup from 'config/yup.custom';
import { STORAGE_KEY } from 'constants/global';
import { SYS_MESS } from 'constants/systemMessage';
import { cloneDeep } from 'lodash';
import { ADD_CHILD_DATA } from 'models/create-request';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  addChildrenAction,
  deleteChildrenAction,
  getChildrenListAction,
  updateChildrenAction,
} from 'store/reducers/parent/actionTypes';
import {
  updateOverallDataSubmit,
  updateStepTrial,
} from 'store/reducers/trial-request';
import { theme } from 'theme';
import { removeLocalStorage, sleep } from 'utils';
import {
  AddChildButton,
  AnChildItem,
  FormContainer,
  LabelCheckbox,
  LabelWrap,
  MoreActions,
  NextButton,
} from './styles';

const schema = yup
  .object()
  .shape({
    children: yup.array().of(
      yup.object().shape({
        name: yup.string().required(SYS_MESS.ERROR.NAME_OF_CHILD_REQUIRED),
        date_of_birth: yup.mixed().required(SYS_MESS.ERROR.AGE_REQUIRED),
      }),
    ),
    isAccepted: yup
      .boolean()
      .test(
        'isChecked',
        SYS_MESS.ERROR.CHECKBOX_REQUIRED,
        (value) => value === true,
      )
      .required(SYS_MESS.ERROR.CHECKBOX_REQUIRED),
  })
  .required();

function RequestAddChildren() {
  const dispatch = useAppDispatch();
  const { overallDataSubmit } = useAppSelector((state) => state.baseRequest);
  const { childrenList } = useAppSelector((state) => state.parent);

  const form = useForm<ADD_CHILD_DATA>({
    resolver: yupResolver(schema),
    defaultValues: {
      children: [{ id: '', name: '', date_of_birth: null }],
      isAccepted: false,
    },
  });

  const { handleSubmit, control, setValue, watch } = form;
  const { append, remove } = useFieldArray({
    control,
    name: 'children',
  });

  const addChild = () => {
    append({ id: '', name: '', date_of_birth: null });
  };

  const removeChild = async (index: number, id: string) => {
    if (!id) remove(index);
    else
      dispatch(
        deleteChildrenAction({
          data: { id },
          onSuccess: () => {
            dispatch(getChildrenListAction());
          },
        }),
      );
  };

  const numberChild = document.getElementById('number-child')?.offsetWidth;
  const deleteChild = document.getElementById('delete-child')?.offsetWidth;
  const widthCenter = useMemo(() => {
    if (typeof (numberChild + deleteChild) !== 'number') return 0;
    return `calc(100% - ${numberChild + deleteChild + 32}px)`;
  }, [numberChild, deleteChild]);

  const onSubmit = async (data: ADD_CHILD_DATA) => {
    const newChildrenNoIds = cloneDeep(
      data.children.filter((item) => item.id === ''),
    );

    // Combine new children without IDs and old children without date of birth
    const childrenPromises1 = newChildrenNoIds.map((child) =>
      dispatch(
        addChildrenAction({
          data: {
            full_name: child.name,
            date_of_birth: child.date_of_birth,
          },
        }),
      ),
    );

    // Update the children list with the old children that don't have date of birth
    const childrenPromises2 = childrenList.map((child) =>
      dispatch(
        updateChildrenAction({
          data: {
            id: child.id,
            full_name: child.full_name,
            date_of_birth: data.children.find((c) => c.id === child.id)
              ?.date_of_birth,
          },
        }),
      ),
    );

    await Promise.all(childrenPromises1.concat(childrenPromises2)).catch(
      (e) => {
        console.error('Error updating children:', e);
      },
    );

    await sleep(500);
    dispatch(updateOverallDataSubmit({ ...overallDataSubmit, addChild: data }));
    dispatch(updateStepTrial(2));
  };

  useEffect(() => {
    removeLocalStorage(STORAGE_KEY.CREATE_REQUEST_DATA_FILLED);
    dispatch(getChildrenListAction());
  }, [dispatch]);

  useEffect(() => {
    if (!childrenList) return;
    const _childrenList = childrenList?.map((child) => {
      return {
        id: child.id,
        name: child.full_name,
        date_of_birth: child.date_of_birth,
      };
    });

    setValue('children', _childrenList);
    setValue('isAccepted', overallDataSubmit.addChild.isAccepted);
  }, [childrenList, overallDataSubmit, setValue]);

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <LabelWrap>
        <Typography variant="Small_Body_16" width={'48%'}>
          Name of Child
        </Typography>
        <Typography variant="Small_Body_16" width={'48%'}>
          Date of Birth
        </Typography>
      </LabelWrap>
      {watch('children').map((child, index) => {
        return (
          <AnChildItem key={child.id}>
            <Typography
              variant="Components_UnstyledContainer"
              id="number-child"
              sx={{ opacity: 0.3 }}
            >
              #{index + 1}
            </Typography>
            <InputField
              control={control}
              name={`children.${index}.name`}
              placeholder="What is your child’s name?"
            />
            <DatePickerField
              name={`children.${index}.date_of_birth`}
              control={control}
              iconColor="primary"
              placeholder="Date of birthday"
              maxDate={moment()}
            />
            <IconButton
              id="delete-child"
              sx={{ padding: 0 }}
              onClick={() => removeChild(index, child.id)}
            >
              <CloseCircle sx={{ color: theme.palette.grey1.dark }} />
            </IconButton>
          </AnChildItem>
        );
      })}
      <MoreActions width={widthCenter}>
        <AddChildButton
          onClick={addChild}
          $hasChildren={childrenList.length > 0 || watch('children').length > 0}
        >
          + Add another child
        </AddChildButton>
        <Divider sx={{ borderColor: '#D9D9D9' }} />
        <CheckboxField
          name="isAccepted"
          control={control}
          label={
            <LabelCheckbox>
              I acknowledge that I have read and understood the
              <span>
                &nbsp;
                <a
                  href="/Data_Protection_Notice.pdf"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#FF796E' }}
                >
                  Data Protection Notice
                </a>
                &nbsp;
              </span>
              , and consent to the collection, use and disclosure of my personal
              data by Aunty SG for the purposes set out in the Notice.
            </LabelCheckbox>
          }
        />
        <NextButton variant="contained" type="submit">
          Next
        </NextButton>
      </MoreActions>
    </FormContainer>
  );
}

export default RequestAddChildren;
